import { styles } from "@repo/helpers";
import { Button, ButtonTypes, Spinner } from "@repo/ui";
import { Menu, Radius } from "lucide-react";

import type { Panel } from "./panels";

import { NEW_MAP_ID, useMapConfig } from "./config/mapConfigContext";
import { isLayerVisible } from "./layers/layer";
import { useMapLayers } from "./layers/layerContext";
import { PANEL_IDS } from "./panels";

interface MapToolbarProps {
	openPanel: Panel | null;
	onTogglePanel: (panel: Panel) => void;
	pinCount: number;
	geoMiles: number | undefined;
	loading: boolean;
}

export function MapToolbar({ openPanel, onTogglePanel, pinCount, geoMiles, loading }: MapToolbarProps) {
	const { currentConfig, mapConfigId } = useMapConfig();
	const { layers } = useMapLayers();

	const title = currentConfig?.name ?? (mapConfigId === NEW_MAP_ID ? "New map" : "Maps");
	const counts = `${plural(layers.data.filter(isLayerVisible).length, "layer")} · ${plural(pinCount, "pin")}`;
	const summary =
		mapConfigId === null
			? "Choose a map to get started"
			: geoMiles === undefined
				? counts
				: `${counts} within ${geoMiles.toLocaleString()} mi`;

	return (
		<header className="flex h-14 shrink-0 items-center gap-1 border-b border-border bg-background px-1 lg:hidden">
			<Button
				types={[ButtonTypes.Text]}
				onClick={() => onTogglePanel("layers")}
				aria-label="Maps and layers"
				aria-expanded={openPanel === "layers"}
				aria-controls={PANEL_IDS.layers}
				className="flex h-11 w-11 shrink-0 items-center justify-center"
			>
				<Menu className="h-5 w-5" />
			</Button>
			<div className="min-w-0 flex-1 px-1">
				<p className="m-0 truncate text-sm font-semibold leading-5 text-foreground">{title}</p>
				<div
					role="status"
					className="flex min-w-0 items-center gap-1.5 text-xs leading-4 text-muted-foreground"
				>
					{loading && <Spinner tag="span" />}
					<span className="truncate">{loading ? "Loading pins…" : summary}</span>
				</div>
			</div>
			<Button
				types={[ButtonTypes.Text]}
				onClick={() => onTogglePanel("search")}
				aria-expanded={openPanel === "search"}
				aria-controls={PANEL_IDS.search}
				className="flex h-11 shrink-0 items-center"
			>
				<span
					className={styles(
						"flex h-9 items-center gap-1.5 rounded-full border px-3 text-sm font-medium",
						geoMiles === undefined ? "border-border" : "border-primary bg-primary/10",
					)}
				>
					<Radius className="h-4 w-4" aria-hidden />
					Area
				</span>
			</Button>
		</header>
	);
}

function plural(count: number, noun: string) {
	return `${count.toLocaleString()} ${noun}${count === 1 ? "" : "s"}`;
}
